/**
 * SP-API Sync V2 — Order Items sheet (Path B).
 * SoT: spapi_v2_order_items (ASIN / SellerSKU). Not MarketSync v1.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  amazonConnectApi,
  type SellerConnection,
  type SpapiV2OrderItem,
} from '../../api/amazonconnect';
import {
  PAGE_SIZE_OPTIONS,
  PlatformDataGrid,
  type DataGridColumn,
  type DataGridMeta,
  type PageSizeOption,
} from '../../components/platform/PlatformDataGrid';
import { getMembershipId, getWorkspaceId } from '../../lib/auth-storage';
import { WorkspaceHeader } from '../../shell';

export function SpapiV2OrderItemsPage() {
  const workspaceKey = getWorkspaceId() ?? 'default';
  const userKey = getMembershipId() ?? 'anon';
  const [params, setParams] = useSearchParams();
  const orderFilter = params.get('order_id') ?? '';
  const [connections, setConnections] = useState<SellerConnection[]>([]);
  const [connectionId, setConnectionId] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<PageSizeOption>(50);
  const [qDraft, setQDraft] = useState('');
  const [items, setItems] = useState<SpapiV2OrderItem[]>([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [latencyMs, setLatencyMs] = useState<number | undefined>();
  const [orderDraft, setOrderDraft] = useState(orderFilter);

  useEffect(() => {
    setOrderDraft(orderFilter);
  }, [orderFilter]);

  useEffect(() => {
    void amazonConnectApi
      .listConnections()
      .then((c) => {
        setConnections(c);
        const urlConn = params.get('connection') || '';
        const pick = urlConn && c.some((x) => x.id === urlConn) ? urlConn : c[0]?.id || '';
        setConnectionId((prev) => prev || pick);
      })
      .catch(() => setConnections([]));
  }, [params]);

  const load = useCallback(async () => {
    if (!connectionId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const t0 = performance.now();
    try {
      const res = await amazonConnectApi.spapiV2OrderItems(
        connectionId,
        orderFilter || undefined,
        page,
        pageSize,
      );
      setLatencyMs(Math.round(performance.now() - t0));
      setItems(res.items ?? []);
      setTotal(res.total ?? 0);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load Sync V2 order items');
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [connectionId, orderFilter, page, pageSize]);

  useEffect(() => {
    void load();
  }, [load]);

  const triggerSync = async () => {
    if (!connectionId) return;
    setSyncing(true);
    try {
      await amazonConnectApi.spapiV2Sync(connectionId, 'order_items');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const applyOrderFilter = () => {
    const next = new URLSearchParams(params);
    if (connectionId) next.set('connection', connectionId);
    const trimmed = orderDraft.trim();
    if (trimmed) next.set('order_id', trimmed);
    else next.delete('order_id');
    setParams(next);
    setPage(1);
  };

  const pages = pageSize > 0 ? Math.max(1, Math.ceil(total / pageSize)) : 1;
  const meta: DataGridMeta = {
    total_count: total,
    returned_count: items.length,
    page,
    page_size: pageSize,
    pages,
    remaining: Math.max(0, total - (page - 1) * pageSize - items.length),
    has_more: page < pages,
    execution_time_ms: latencyMs,
    source: 'spapi_v2_order_items',
    data_source_badge: 'Sync V2 · order_items',
  };

  const columns: DataGridColumn<SpapiV2OrderItem>[] = useMemo(
    () => [
      {
        id: 'amazon_order_id',
        header: 'Amazon Order ID',
        width: 170,
        stickyLeft: 0,
        render: (r) => (
          <Link
            className="font-mono text-[11px] text-teal-800 underline-offset-2 hover:underline dark:text-teal-300"
            to={`/amazon/spapi-v2/orders?connection=${encodeURIComponent(connectionId)}`}
          >
            {r.amazon_order_id}
          </Link>
        ),
        exportValue: (r) => r.amazon_order_id,
      },
      {
        id: 'asin',
        header: 'ASIN',
        width: 120,
        render: (r) => <span className="font-mono text-[11px] font-medium">{r.asin || '—'}</span>,
        exportValue: (r) => r.asin ?? '',
      },
      {
        id: 'seller_sku',
        header: 'SKU',
        width: 140,
        render: (r) => <span className="font-mono text-[11px]">{r.seller_sku || '—'}</span>,
        exportValue: (r) => r.seller_sku ?? '',
      },
      {
        id: 'title',
        header: 'Title',
        width: 220,
        render: (r) => <span className="line-clamp-2 text-[11px]">{r.title || '—'}</span>,
        exportValue: (r) => r.title ?? '',
      },
      {
        id: 'qty_ordered',
        header: 'Qty',
        width: 70,
        render: (r) => String(r.qty_ordered ?? 0),
        exportValue: (r) => String(r.qty_ordered ?? 0),
      },
      {
        id: 'item_price',
        header: 'Item price',
        width: 100,
        render: (r) =>
          r.currency ? `${r.currency} ${Number(r.item_price ?? 0).toFixed(2)}` : String(r.item_price ?? '—'),
        exportValue: (r) => String(r.item_price ?? ''),
      },
      {
        id: 'order_item_id',
        header: 'Item ID',
        width: 140,
        render: (r) => <span className="font-mono text-[10px]">{r.order_item_id}</span>,
        exportValue: (r) => r.order_item_id,
      },
    ],
    [connectionId],
  );

  return (
    <div className="mx-auto max-w-[100rem] space-y-4">
      <WorkspaceHeader
        title="Sync V2 Order Items"
        subtitle="Greenfield SP-API lines — ASIN and SKU from getOrderItems. Prefer orders still missing lines."
        breadcrumb={[
          { label: 'Founder OS', path: '/command' },
          { label: 'Commerce' },
          { label: 'Sync V2 Order Items' },
        ]}
        primaryActions={
          <button
            type="button"
            disabled={!connectionId || syncing}
            onClick={() => void triggerSync()}
            className="rounded-md bg-[var(--shell-fg)] px-3 py-2 text-sm font-medium text-[var(--shell-canvas)] disabled:opacity-50"
          >
            {syncing ? 'Syncing…' : 'Pull order items'}
          </button>
        }
      />

      <div className="flex flex-wrap items-center gap-2 text-xs text-[var(--shell-fg-muted)]">
        <label className="flex items-center gap-2">
          Order ID
          <input
            className="w-52 rounded-md border border-[var(--shell-border)] bg-[var(--shell-surface)] px-2 py-1 font-mono text-[11px]"
            value={orderDraft}
            onChange={(e) => setOrderDraft(e.target.value)}
            placeholder="171-…"
          />
        </label>
        <button
          type="button"
          className="rounded-md border border-[var(--shell-border)] px-2 py-1"
          onClick={applyOrderFilter}
        >
          Filter
        </button>
        <Link
          className="underline-offset-2 hover:underline"
          to={`/amazon/spapi-v2/orders?connection=${encodeURIComponent(connectionId)}`}
        >
          ← Sync V2 Orders
        </Link>
        <Link className="text-[var(--shell-fg-muted)] underline-offset-2 hover:underline" to="/amazon/order-items">
          v1 Order Items
        </Link>
      </div>

      <PlatformDataGrid
        gridId="spapi-v2-order-items"
        workspaceKey={workspaceKey}
        userKey={userKey}
        columns={columns}
        rows={items}
        rowKey={(r) => `${r.amazon_order_id}:${r.order_item_id}`}
        meta={meta}
        loading={loading}
        error={error}
        emptyMessage={
          orderFilter
            ? `No Sync V2 lines for ${orderFilter} yet — pull order items (missing-line priority).`
            : 'No Sync V2 order items yet — pull items or wait for the 10m tick.'
        }
        page={page}
        pageSize={pageSize}
        searchDraft={qDraft}
        onSearchDraftChange={setQDraft}
        onSearch={() => setPage(1)}
        onPageChange={setPage}
        onPageSizeChange={(n) => {
          if (PAGE_SIZE_OPTIONS.includes(n)) {
            setPageSize(n);
            setPage(1);
          }
        }}
        onRefresh={() => void load()}
        density="compact"
        toolbarExtra={
          <label className="text-sm">
            <span className="text-xs font-medium text-slate-500">Connection</span>
            <select
              className="mt-1 block min-h-11 min-w-[16rem] rounded-lg border border-slate-300 px-2 dark:border-slate-600 dark:bg-slate-900"
              value={connectionId}
              onChange={(e) => {
                setConnectionId(e.target.value);
                setPage(1);
              }}
            >
              {connections.length === 0 && <option value="">No connections</option>}
              {connections.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.seller_ref || c.id.slice(0, 8)} · {c.status}
                </option>
              ))}
            </select>
          </label>
        }
      />
    </div>
  );
}
